import { type MarketingLocale, useMarketingLocale } from '~/lib/marketing-locale'
import { BubbleIcon, ThumbIcon } from './marketing-glyphs'
import { ProjectIcon } from './product-icons'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/* Projects deep-dive, cream-bg section with a project workspace rebuild.
 * Copy is locale-keyed — see the note in `crm-section.tsx`. */

type Card3 = { title: string; body: string }

type ForumContent = {
  heading: string
  tagline: string
  projectName: string
  followLabel: string
  composerPlaceholder: string
  publishLabel: string
  posts: readonly { name: string; seed: number }[]
  dateFormat: string
  teamLabel: string
  aboutLabel: string
  filesLabel: string
  cards: readonly [Card3, Card3, Card3]
}

const CONTENT: Record<MarketingLocale, ForumContent> = {
  en: {
    aboutLabel: 'About',
    cards: [
      {
        body: 'Every project opens with Permits, Survey, Construction Documents and more — the structure the practice already uses.',
        title: 'Folders that arrive ready',
      },
      {
        body: 'Client, deadline, and project type in plain sight. Residential or commercial, active or on hold, the state of the project reads immediately.',
        title: 'The whole project in one place',
      },
      {
        body: 'The current sheet on top, the revision history right below it. Nothing gets lost between one issue and the next.',
        title: 'Every revision kept',
      },
    ],
    composerPlaceholder: 'Write an update…',
    dateFormat: 'May 2',
    filesLabel: 'Files',
    followLabel: 'Follow',
    heading: 'Projects',
    posts: [
      { name: 'Maya Ellis', seed: 1 },
      { name: 'Ryan Cole', seed: 4 },
    ],
    projectName: 'Whitfield House',
    publishLabel: 'Post',
    tagline: 'Every job with a space of its own',
    teamLabel: 'Team',
  },
  'pt-BR': {
    aboutLabel: 'Sobre',
    cards: [
      {
        body: 'Todo projeto começa com Alvará, Levantamento, Projeto Executivo e mais, a estrutura que o escritório já usa.',
        title: 'Pastas que já vêm prontas',
      },
      {
        body: 'Cliente, prazo e tipo de obra à vista. Residencial ou comercial, em andamento ou em espera, o estado do projeto fica claro de imediato.',
        title: 'O projeto inteiro num lugar só',
      },
      {
        body: 'A versão atual da prancha em cima, o histórico de revisões logo abaixo. Nada se perde entre uma entrega e outra.',
        title: 'Cada revisão guardada',
      },
    ],
    composerPlaceholder: 'Escreva uma atualização…',
    dateFormat: '2',
    filesLabel: 'Arquivos',
    followLabel: 'Seguir',
    heading: 'Projetos',
    posts: [
      { name: 'Marina Alves', seed: 1 },
      { name: 'Rafael Souza', seed: 4 },
    ],
    projectName: 'Residência Lopes',
    publishLabel: 'Publicar',
    tagline: 'Cada obra com o seu próprio espaço',
    teamLabel: 'Equipe',
  },
}

const TONE = ['#e8b4a0', '#a9c4e8', '#bcd6a8', '#e8c98a', '#c9b0e0', '#90c9bd']

const Avatar = ({ seed, size = 26 }: { seed: number; size?: number }) => (
  <span
    className="inline-block shrink-0 rounded-full"
    style={{ background: TONE[seed % TONE.length], height: size, width: size }}
  />
)

const Line = ({ w }: { w: string }) => (
  <span className="block h-[7px] rounded-full bg-[#e9e9eb]" style={{ width: w }} />
)

/* pt-BR renders "21 mai · 10:20"; English renders "May 21 · 10:20". The day
 * number varies per post, so each locale supplies the surrounding shape. */
const postDate = (locale: MarketingLocale, format: string, index: number) =>
  locale === 'en'
    ? `${format}${index + 1} · 10:2${index}`
    : `${format}${index + 1} mai · 10:2${index}`

const ForumMockup = ({ content, locale }: { content: ForumContent; locale: MarketingLocale }) => (
  <div className="overflow-hidden rounded-2xl border border-[#ece6d8] bg-white shadow-[0_24px_60px_-26px_rgba(80,60,20,0.28)]">
    <div className="flex">
      <div className="flex-1 border-[#f1f1f1] border-r">
        <div className="flex items-center justify-between border-[#f1f1f1] border-b px-5 py-3">
          <span className="font-semibold text-[#1c1c20] text-[13px]">{content.projectName}</span>
          <span className="rounded-md bg-[#1d1d20] px-2 py-1 text-[10px] text-white">
            {content.followLabel}
          </span>
        </div>
        <div className="px-5 py-3">
          <div className="flex items-center gap-2 rounded-lg border border-[#eee] px-3 py-2">
            <span className="text-[#b4b4b9] text-[12px]">{content.composerPlaceholder}</span>
            <span className="ml-auto rounded-md bg-[#5b8def] px-2.5 py-1 text-[10px] text-white">
              {content.publishLabel}
            </span>
          </div>
        </div>
        {content.posts.map((post, i) => (
          <div className="border-[#f3f3f3] border-t px-5 py-4" key={post.name}>
            <div className="flex items-center gap-2">
              <Avatar seed={post.seed} />
              <div>
                <p className="font-semibold text-[#1c1c20] text-[12px]">{post.name}</p>
                <p className="text-[#b4b4b9] text-[10.5px]">
                  {postDate(locale, content.dateFormat, i)}
                </p>
              </div>
            </div>
            <div className="mt-2.5 space-y-1.5 pl-[34px]">
              <Line w="100%" />
              <Line w="92%" />
              <Line w="60%" />
            </div>
            <div className="mt-2.5 flex items-center gap-3 pl-[34px] text-[#a3a3a8] text-[11px]">
              <span className="inline-flex items-center gap-1 rounded-full bg-[#f1f1f1] px-2 py-0.5">
                <ThumbIcon className="size-3" /> {i + 3}
              </span>
              <span className="inline-flex items-center gap-1">
                <BubbleIcon className="size-3" /> {i * 8 + 6}
              </span>
            </div>
          </div>
        ))}
      </div>
      <aside className="hidden w-[200px] shrink-0 p-4 sm:block">
        <p className="text-[#9a9aa0] text-[11px]">{content.teamLabel}</p>
        <div className="mt-1.5 flex">
          {[0, 1, 2, 3, 4].map((s) => (
            <span className="-ml-1.5 first:ml-0" key={s}>
              <Avatar seed={s} size={22} />
            </span>
          ))}
        </div>
        <p className="mt-4 text-[#9a9aa0] text-[11px]">{content.aboutLabel}</p>
        <div className="mt-1.5 space-y-1.5">
          <Line w="100%" />
          <Line w="88%" />
          <Line w="70%" />
        </div>
        <p className="mt-4 text-[#9a9aa0] text-[11px]">{content.filesLabel}</p>
        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
          {[
            ['#9fc3e8', '#7fae8a'],
            ['#c4d9a8', '#a8b878'],
            ['#a8c8e8', '#cdd9e8'],
            ['#d8c98a', '#9aa878'],
          ].map(([a, b]) => (
            <span
              className="block h-9 rounded-md"
              key={a}
              style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}
            />
          ))}
        </div>
      </aside>
    </div>
  </div>
)

const Card = ({
  title,
  body,
  children,
  heading: CardHeading = 'h3',
}: {
  title: string
  body: string
  children: React.ReactNode
  /** One level below the section heading: `h2` when the section renders `h1`. */
  heading?: 'h2' | 'h3'
}) => (
  <div className="rounded-2xl border border-[#ece6d8] bg-[#fffdf7] p-6">
    <CardHeading className="font-bold text-[#1c1c20] text-[17px]">{title}</CardHeading>
    <p className="mt-2 text-[#8a8a90] text-[14.5px] leading-relaxed">{body}</p>
    <div className="mt-5">{children}</div>
  </div>
)

export const ForumSection = ({
  headingLevel: Heading = 'h2',
}: {
  /** `h1` when the section opens a deep-dive page and owns its top heading. */
  headingLevel?: 'h1' | 'h2'
}) => {
  const locale = useMarketingLocale()
  const content = CONTENT[locale]
  const cardHeading = Heading === 'h1' ? 'h2' : 'h3'

  return (
    <section className="relative overflow-hidden bg-[#fdf6e7] py-28">
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="forum"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <div className="flex items-center justify-center gap-3">
            <ProjectIcon className="size-[56px]" />
            <Heading className="font-display text-[#0b0b0d] text-[clamp(48px,8vw,76px)] tracking-[-0.01em]">
              {content.heading}
            </Heading>
          </div>
          <p className="mt-3 text-[#8a8a90] text-[clamp(18px,2.6vw,24px)]">{content.tagline}</p>
        </ScrollReveal>

        <ScrollReveal className="mt-14" delay={100}>
          <ForumMockup content={content} locale={locale} />
        </ScrollReveal>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          <ScrollReveal>
            <Card body={content.cards[0].body} heading={cardHeading} title={content.cards[0].title}>
              <div className="space-y-2 rounded-lg border border-[#eee] bg-white p-2">
                {[0, 4].map((s) => (
                  <div className="flex items-center gap-2" key={s}>
                    <Avatar seed={s} size={18} />
                    <Line w="70%" />
                  </div>
                ))}
              </div>
            </Card>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <Card body={content.cards[1].body} heading={cardHeading} title={content.cards[1].title}>
              <div className="rounded-lg border border-[#eee] bg-white p-3">
                <div className="space-y-1.5">
                  <Line w="100%" />
                  <Line w="80%" />
                </div>
                <div className="mt-2 flex items-center gap-2 text-[#a3a3a8] text-[10px]">
                  <span className="inline-flex items-center gap-1">
                    <ThumbIcon className="size-3" /> 0
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1">
                    <BubbleIcon className="size-3" /> 4
                  </span>
                </div>
              </div>
            </Card>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <Card body={content.cards[2].body} heading={cardHeading} title={content.cards[2].title}>
              <div className="space-y-2 rounded-lg border border-[#eee] bg-white p-2">
                {[2, 3].map((s) => (
                  <div className="flex items-center gap-2" key={s}>
                    <Avatar seed={s} size={18} />
                    <Line w="64%" />
                  </div>
                ))}
              </div>
            </Card>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
